import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import useIonicToast from '../../../hooks/useIonicToast';
import { RootState } from '../../../store/store';
import { Conversation } from '../../../types/Messaging/Conversation';
import { getConversations } from '../getConversations';
import { useSocket } from '../../../context/SocketContext';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { NewConversationPayload } from '../../../types/Messaging/NewConversationPayload';
import { createNewConversation } from '../createNewConversation';
import { getUnreadCounts } from '../getUnreadCounts';
import '../../../utils/polyfills/promiseAllSettled';

export const useConversations = () => {
  const { signedAccessToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const history = useHistory();
  const { presentIonToast } = useIonicToast();
  const queryClient = useQueryClient();

  // CREATE CONVERSATION IF NEW CONVERSATION PAYLOAD IS PASSED TO ROUTE STATE
  const location = useLocation();
  const newConPayload = location.state as NewConversationPayload | undefined;
  const shouldCreateNewCon =
    Boolean(newConPayload) &&
    Boolean(newConPayload?.type) &&
    Boolean(newConPayload?.meta_data?.participants?.length);
  const newConversationCreated = useRef<Boolean>(false);

  //SOCKET CONTEXT
  const {
    connect: connectToSocket,
    disconnect: disconnectFromSocket,
    isConnected,
  } = useSocket();

  useEffect(() => {
    if (signedAccessToken) connectToSocket(signedAccessToken);
    return () => {
      if (isConnected) disconnectFromSocket();
    };
  }, [signedAccessToken]);

  const fetchConversations = async (): Promise<
    Array<Conversation & { unread_count: number }> | undefined
  > => {
    if (!signedAccessToken) return;
    try {
      let res: Array<Conversation & { unread_count: number }> = [];

      const [conversationsRes, conUnreadCountRes] = await Promise.allSettled([
        getConversations(signedAccessToken),
        getUnreadCounts(signedAccessToken),
      ]);

      if (conversationsRes.status === 'rejected') {
        // If fetching conversations failed, propagate its reason so the query can handle it
        throw conversationsRes.reason;
      }

      // Build a map of conversationId -> unread count when available
      const unreadMsgMap: Map<number, number> = new Map();

      if (
        conUnreadCountRes.status === 'fulfilled' &&
        conUnreadCountRes.value?.unreadByConversation
      ) {
        conUnreadCountRes.value.unreadByConversation.forEach((item: any) => {
          // Support multiple possible count key names from the API
          const count =
            item.unread_count ?? item.unreadCount ?? item.count ?? 0;
          unreadMsgMap.set(item.conversationId, count);
        });
      } else if (conUnreadCountRes.status === 'rejected') {
        // If unread counts failed, log a warning but still return conversations
        console.warn(
          'Failed to fetch unread counts:',
          conUnreadCountRes.reason
        );
      }

      // Merge unread counts into conversations
      res = (conversationsRes.value as Array<Conversation>).map((c) => ({
        ...c,
        unread_count: unreadMsgMap.get(Number((c as any).id)) ?? 0,
      }));

      return res;
    } catch (error: any) {
      presentIonToast(error?.message ?? String(error), 'error');
      throw error;
    }
  };

  const { data: query_data, ...conversationsQuery } = useQuery({
    queryKey: ['conversations'],
    queryFn: fetchConversations,
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  });

  // AUTOMATICALLY ATTEMPT TO CREATE NEW USER WHEN ROUTE HAS A ROUTE STATE
  const createConversation = async () => {
    try {
      if (!shouldCreateNewCon || !signedAccessToken || !newConPayload) return;
      const res: Conversation = await createNewConversation(
        newConPayload,
        signedAccessToken
      );
      if (res?.id) history.push(`/dashboard/chat/${res?.id}`);
      newConversationCreated.current = true;
      await queryClient.invalidateQueries({
        queryKey: ['conversations'],
        exact: false,
      });
      return res;
    } catch (error: any) {
      presentIonToast(error.message);
    }
  };
  useQuery({
    queryKey: ['new-conversation', newConPayload],
    queryFn: createConversation,
    enabled: shouldCreateNewCon,
  });

  return { ...conversationsQuery, conversations: query_data };
};
